import React, { useState } from 'react';
import {
  Button,
  List,
  Card,
  Space,
  Typography,
  Select,
  Input,
  TimePicker,
  Form,
  message,
  Popconfirm,
  Divider,
} from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

// Types
export type ScheduleType = 'hourly' | 'daily' | 'weekly' | 'cron';

export interface Schedule {
  id: string;
  name: string;
  type: ScheduleType;
  minute?: number;
  hour?: number;
  weekday?: number;
  cron?: string;
  description: string;
}

// Restore 10 dummy schedules
const dummySchedules: Schedule[] = [
  {
    id: '1',
    name: 'Hourly at :15',
    type: 'hourly',
    minute: 15,
    description: 'Runs every hour at 15 minutes past',
  },
  {
    id: '2',
    name: 'Daily at 02:30',
    type: 'daily',
    hour: 2,
    minute: 30,
    description: 'Runs every day at 02:30',
  },
  {
    id: '3',
    name: 'Weekly on Monday 09:00',
    type: 'weekly',
    weekday: 1,
    hour: 9,
    minute: 0,
    description: 'Runs every Monday at 09:00',
  },
  {
    id: '4',
    name: 'Every Friday at 17:45',
    type: 'weekly',
    weekday: 5,
    hour: 17,
    minute: 45,
    description: 'Runs every Friday at 17:45',
  },
  {
    id: '5',
    name: 'Custom Cron: 0 12 * * 1-5',
    type: 'cron',
    cron: '0 12 * * 1-5',
    description: 'Runs at noon on weekdays',
  },
  {
    id: '6',
    name: 'Hourly at :00',
    type: 'hourly',
    minute: 0,
    description: 'Runs every hour on the hour',
  },
  {
    id: '7',
    name: 'Daily at 23:59',
    type: 'daily',
    hour: 23,
    minute: 59,
    description: 'Runs every day at 23:59',
  },
  {
    id: '8',
    name: 'Weekly on Sunday 08:00',
    type: 'weekly',
    weekday: 0,
    hour: 8,
    minute: 0,
    description: 'Runs every Sunday at 08:00',
  },
  {
    id: '9',
    name: 'Custom Cron: 30 6 * * *',
    type: 'cron',
    cron: '30 6 * * *',
    description: 'Runs every day at 06:30',
  },
  {
    id: '10',
    name: 'Weekly on Wednesday 15:15',
    type: 'weekly',
    weekday: 3,
    hour: 15,
    minute: 15,
    description: 'Runs every Wednesday at 15:15',
  },
];

// Placeholder API functions
const fetchSchedules = async (): Promise<Schedule[]> => Promise.resolve(dummySchedules);
const createSchedule = async (schedule: Schedule) => message.success('Schedule created (placeholder)');
const updateSchedule = async (schedule: Schedule) => message.success('Schedule updated (placeholder)');
const deleteSchedule = async (id: string) => message.success('Schedule deleted (placeholder)');
const duplicateSchedule = async (schedule: Schedule) => message.success('Schedule duplicated (placeholder)');

// Helper for human readable schedule
const getScheduleDescription = (s: Schedule) => {
  switch (s.type) {
    case 'hourly':
      return `Every hour at :${s.minute?.toString().padStart(2, '0')}`;
    case 'daily':
      return `Every day at ${s.hour?.toString().padStart(2, '0')}:${s.minute?.toString().padStart(2, '0')}`;
    case 'weekly':
      return `Every ${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][s.weekday ?? 0]} at ${s.hour?.toString().padStart(2, '0')}:${s.minute?.toString().padStart(2, '0')}`;
    case 'cron':
      return `Cron: ${s.cron}`;
    default:
      return '';
  }
};

interface SchedulerPanelProps {
  visible: boolean;
  onClose: () => void;
}

const SchedulerPanel: React.FC<SchedulerPanelProps> = ({ visible, onClose }) => {
  const [schedules, setSchedules] = useState<Schedule[]>([...dummySchedules]);
  const [loading] = useState(false);
  const [selected, setSelected] = useState<Schedule | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [form] = Form.useForm();

  const handleCreate = () => {
    setFormMode('create');
    setSelected(null);
    form.resetFields();
    setFormVisible(true);
  };

  const handleEdit = (schedule: Schedule) => {
    setFormMode('edit');
    setSelected(schedule);
    form.setFieldsValue({
      ...schedule,
      time: schedule.hour !== undefined && schedule.minute !== undefined ? dayjs().hour(schedule.hour).minute(schedule.minute) : undefined,
    });
    setFormVisible(true);
  };

  const handleDuplicate = (schedule: Schedule) => {
    setFormMode('duplicate');
    setSelected(schedule);
    form.setFieldsValue({
      ...schedule,
      name: schedule.name + ' (Copy)',
      time: schedule.hour !== undefined && schedule.minute !== undefined ? dayjs().hour(schedule.hour).minute(schedule.minute) : undefined,
    });
    setFormVisible(true);
  };

  const handleDelete = async (id: string) => {
    await deleteSchedule(id);
    setSchedules((prev) => prev.filter((s) => s.id !== id));
  };

  const handleFormFinish = async (values: any) => {
    let newSchedule: Schedule = {
      id: selected?.id || Math.random().toString(36).substr(2, 9),
      name: values.name,
      type: values.type,
      description: '',
    };
    if (values.type === 'hourly') {
      newSchedule.minute = values.minute;
      newSchedule.description = `Every hour at :${values.minute.toString().padStart(2, '0')}`;
    } else if (values.type === 'daily') {
      newSchedule.hour = values.time.hour();
      newSchedule.minute = values.time.minute();
      newSchedule.description = `Every day at ${values.time.format('HH:mm')}`;
    } else if (values.type === 'weekly') {
      newSchedule.weekday = values.weekday;
      newSchedule.hour = values.time.hour();
      newSchedule.minute = values.time.minute();
      newSchedule.description = `Every ${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][values.weekday]} at ${values.time.format('HH:mm')}`;
    } else if (values.type === 'cron') {
      newSchedule.cron = values.cron;
      newSchedule.description = `Cron: ${values.cron}`;
    }
    if (formMode === 'create' || formMode === 'duplicate') {
      await createSchedule(newSchedule);
      setSchedules((prev) => [...prev, newSchedule]);
    } else if (formMode === 'edit') {
      await updateSchedule(newSchedule);
      setSchedules((prev) => prev.map((s) => (s.id === newSchedule.id ? newSchedule : s)));
    }
    setFormVisible(false);
  };

  return (
    <div style={{ height: '100%', padding: 24, overflow: 'auto' }}>
      <Title level={4}>Scheduler</Title>
      <Divider />
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Button type="primary" onClick={handleCreate} style={{ alignSelf: 'flex-end' }}>
          Create Schedule
        </Button>
        {formVisible && (
          <Card
            title={formMode === 'edit' ? 'Edit Schedule' : formMode === 'duplicate' ? 'Duplicate Schedule' : 'Create Schedule'}
            variant="borderless"
            style={{ width: '100%' }}
            extra={<Button onClick={() => setFormVisible(false)}>Close</Button>}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFormFinish}
              initialValues={{ type: 'hourly', minute: 0, weekday: 1 }}
            >
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}> 
                <Input />
              </Form.Item>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}> 
                <Select>
                  <Option value="hourly">Hourly</Option>
                  <Option value="daily">Daily</Option>
                  <Option value="weekly">Weekly</Option>
                  <Option value="cron">Custom (Cron)</Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(prev, curr) => prev.type !== curr.type} noStyle>
                {({ getFieldValue }) => {
                  const type = getFieldValue('type');
                  if (type === 'hourly') {
                    return (
                      <Form.Item name="minute" label="Minute (0-59)" rules={[{ required: true, type: 'number', min: 0, max: 59 }]}> 
                        <Input type="number" min={0} max={59} />
                      </Form.Item>
                    );
                  } else if (type === 'daily') {
                    return (
                      <Form.Item name="time" label="Time" rules={[{ required: true }]}> 
                        <TimePicker format="HH:mm" />
                      </Form.Item>
                    );
                  } else if (type === 'weekly') {
                    return (
                      <>
                        <Form.Item name="weekday" label="Day of Week" rules={[{ required: true }]}> 
                          <Select>
                            <Option value={0}>Sunday</Option>
                            <Option value={1}>Monday</Option>
                            <Option value={2}>Tuesday</Option>
                            <Option value={3}>Wednesday</Option>
                            <Option value={4}>Thursday</Option>
                            <Option value={5}>Friday</Option>
                            <Option value={6}>Saturday</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item name="time" label="Time" rules={[{ required: true }]}> 
                          <TimePicker format="HH:mm" />
                        </Form.Item>
                      </>
                    );
                  } else if (type === 'cron') {
                    return (
                      <Form.Item name="cron" label="Cron Expression" rules={[{ required: true }]}> 
                        <Input placeholder="e.g. 0 12 * * 1-5" />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  {formMode === 'edit' ? 'Save' : 'Create'}
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={() => setFormVisible(false)}>
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </Card>
        )}
        {formVisible && <Divider style={{ margin: '16px 0', background: '#e0e0e0' }} />}
        <Card title="Schedules" variant="borderless" style={{ width: '100%' }}>
          <List
            loading={loading}
            dataSource={schedules}
            renderItem={(item) => (
              <List.Item
                onClick={() => setSelected(item)}
                style={{
                  cursor: 'pointer',
                  background: selected && selected.id === item.id ? '#e6f7ff' : undefined,
                  borderRadius: 4,
                  transition: 'background 0.2s',
                }}
                actions={[
                  <Button size="small" onClick={() => handleEdit(item)} key="edit">Edit</Button>,
                  <Button size="small" onClick={() => handleDuplicate(item)} key="duplicate">Duplicate</Button>,
                  <Popconfirm
                    title="Delete schedule?"
                    onConfirm={() => handleDelete(item.id)}
                    okText="Yes"
                    cancelText="No"
                    key="delete"
                  >
                    <Button size="small" danger>Delete</Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={<Text strong>{item.name}</Text>}
                  description={getScheduleDescription(item)}
                />
              </List.Item>
            )}
          />
        </Card>
      </Space>
    </div>
  );
};

export default SchedulerPanel; 